/**
 * Auth Callback Page
 *
 * Handles authentication callbacks from Supabase (email confirmation, magic links, OAuth).
 * Extracts the session from the URL and redirects the user.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';

export function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get the hash fragment from the URL (contains auth tokens)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const type = hashParams.get('type');

    if (accessToken && refreshToken) {
      // Set the session
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      }).then(({ error }) => {
        if (error) {
          console.error('Auth callback error:', error);
          setError(error.message);
        } else {
          // Successfully authenticated
          console.log('✅ Authentication successful');

          // Redirect based on type
          if (type === 'signup') {
            navigate('/', { replace: true });
          } else {
            // Default redirect
            navigate('/', { replace: true });
          }
        }
      });
    } else {
      // No tokens in URL - might be an error
      const errorDescription = hashParams.get('error_description');
      if (errorDescription) {
        setError(errorDescription);
      } else {
        // No error, just redirect home
        navigate('/', { replace: true });
      }
    }
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold mb-4 text-destructive">
              Authentication Error
            </h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90"
            >
              Go Home
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Signing you in...</h2>
          <p className="text-sm text-muted-foreground">
            Please wait while we complete your authentication
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
